import { useState, useEffect } from 'react';
import { merchantLogoUrl, merchantLogoFallbackUrl } from '../../utils/merchantLogo';

/**
 * Renders a merchant logo with an automatic fallback chain (see merchantLogoUrl):
 *   1. logo_url from DB — but a dead host (Clearbit) or a DuckDuckGo ip3 favicon
 *      (which 404s noisily) is re-pointed at Google's favicon service.
 *   2. Google favicon API derived from the website domain.
 *   3. Brand-coloured initial-letter avatar (covers every remaining failure).
 */
export default function MerchantLogo({ merchant, size = 40, radius, style = {}, className }) {
  const primary = merchantLogoUrl(merchant);
  const fallback = merchantLogoFallbackUrl(merchant);
  const [src, setSrc] = useState(primary);
  const [stage, setStage] = useState(0);

  useEffect(() => {
    setSrc(primary);
    setStage(0);
  }, [primary]);

  const r = radius ?? Math.round(size * 0.22);
  const baseStyle = {
    width: size,
    height: size,
    borderRadius: r,
    display: 'block',
    flexShrink: 0,
    ...style,
  };

  const handleError = () => {
    if (stage === 0 && fallback && fallback !== src) {
      setSrc(fallback);
      setStage(1);
    } else {
      setSrc(null);
    }
  };

  if (src) {
    return (
      <img
        src={src}
        alt={merchant?.name || ''}
        onError={handleError}
        className={className}
        style={{
          objectFit: 'contain',
          border: '1px solid #E4E7F0',
          padding: Math.max(2, Math.round(size * 0.07)),
          background: '#fff',
          ...baseStyle,
        }}
      />
    );
  }

  const initial = (merchant?.name || '?').trim().charAt(0).toUpperCase();
  return (
    <div
      className={className}
      aria-label={merchant?.name || 'Merchant'}
      style={{
        ...baseStyle,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        fontWeight: 800,
        fontSize: Math.round(size * 0.42),
        letterSpacing: '-0.02em',
        background: 'linear-gradient(135deg, #6B2FD9 0%, #D946EF 100%)',
      }}
    >
      {initial}
    </div>
  );
}
